import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "../toast";
import {
  getUserNotifications,
  markNotificationRead,
  deleteNotification,
  getUnreadNotificationCount,
  markAllNotificationsRead,
  markAsReadLocally,
  removeNotificationLocally,
} from "../../slice/notifications/notificationsSlice";
import NotificationsPanelView from "./NotificationsPanelView";

// A notification is unread until the user's read state is set. Read state is
// tracked by the backend via `isRead`/`readAt` (camelCase in the API response),
// INDEPENDENT of the delivery `status` — so we must not rely on `status` here.
// Freshly-arrived socket notifications have no read flag yet, so they read unread.
const isNotificationUnread = (n) =>
  !(n.isRead || n.is_read || n.readAt || n.read_at);

const NotificationsPanel = ({ isOpen, onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const { notifications, pagination, unreadCount, loading, error } =
    useSelector((state) => state.notifications);

  // Stored Arabic title / body when the app is in Arabic, English otherwise.
  const getLocalizedContent = (notification) => {
    const isArabic = i18n.language === "ar";
    const titleArabic = notification.titleArabic || notification.title_arabic;
    const bodyArabic = notification.bodyArabic || notification.body_arabic;
    return {
      title:
        (isArabic && titleArabic) ||
        notification.title ||
        t("notifications.fallbackTitle"),
      body: (isArabic && bodyArabic) || notification.body || "",
    };
  };

  // Load notifications on mount and when panel opens
  useEffect(() => {
    if (isOpen) {
      dispatch(getUserNotifications({ page: 1, limit: 20 }));
      dispatch(getUnreadNotificationCount());
    }
  }, [isOpen, dispatch]);

  // Load more notifications
  const loadMore = () => {
    if (!loading.fetch && pagination.page < pagination.totalPages) {
      dispatch(
        getUserNotifications({
          page: pagination.page + 1,
          limit: 20,
        }),
      );
    }
  };

  // Handle mark as read
  const handleMarkAsRead = async (notificationId) => {
    try {
      dispatch(markAsReadLocally(notificationId));
      await dispatch(markNotificationRead(notificationId)).unwrap();
    } catch (error) {
      toast.error("Failed to mark notification as read");
    }
  };

  // Handle delete notification
  const handleDelete = async (notificationId) => {
    try {
      dispatch(removeNotificationLocally(notificationId));
      await dispatch(deleteNotification(notificationId)).unwrap();
    } catch (error) {
      toast.error("Failed to delete notification");
    }
  };

  // Handle mark all as read
  const handleMarkAllAsRead = async () => {
    try {
      const result = await dispatch(markAllNotificationsRead()).unwrap();

      if (result.status === true || result.status === "success") {
        dispatch(getUserNotifications({ page: 1, limit: 20 }));
        dispatch(getUnreadNotificationCount());
        toast.success("All notifications marked as read");
      } else {
        toast.error("Failed to mark all notifications as read");
      }
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
      toast.error("Failed to mark all notifications as read");
    }
  };

  // Always mark as read on click, then open the booking when the payload names one.
  const handleOpen = (notification) => {
    handleMarkAsRead(notification.recipient || notification.id);

    const bookingId =
      notification.data?.booking_id || notification.data?.order_id;
    if (bookingId) {
      onClose();
      navigate(`/imaging_appointment_detail/${bookingId}`);
    }
  };

  const isChatMessage = (notification) =>
    notification.notification_type === "CHAT_MESSAGE" ||
    notification.notificationType === "CHAT_MESSAGE" ||
    notification.title?.toLowerCase() === "new message";

  if (!isOpen) {
    return null;
  }

  return (
    <NotificationsPanelView
      onClose={onClose}
      labels={{
        title: t("notifications.title"),
        unreadCount: (count) => t("notifications.unreadCount", { count }),
        markAllAsRead: t("notifications.markAllAsRead"),
        loading: t("notifications.loading"),
        failedToLoad: t("notifications.failedToLoad"),
        retry: t("notifications.retry"),
        empty: t("notifications.noNotifications"),
        emptyHint: t("notifications.noNotificationsHint"),
        delete: t("notifications.deleteNotification"),
        loadMore: t("notifications.loadMore"),
        loadingMore: t("notifications.loadingMore"),
      }}
      notifications={notifications}
      unreadCount={unreadCount}
      loading={loading.fetch}
      error={Boolean(error.fetch)}
      markingAllRead={loading.markAllRead}
      hasMore={pagination.page < pagination.totalPages}
      onRetry={() => dispatch(getUserNotifications({ page: 1, limit: 20 }))}
      onLoadMore={loadMore}
      onMarkAllAsRead={handleMarkAllAsRead}
      getKey={(n) => n.recipient || n.id}
      getTitle={(n) => getLocalizedContent(n).title}
      getMessage={(n) =>
        isChatMessage(n)
          ? t("notifications.newMessageFromAdmin")
          : getLocalizedContent(n).body
      }
      isUnread={isNotificationUnread}
      onOpen={handleOpen}
      onDelete={(n) => handleDelete(n.recipient)}
    />
  );
};

export default NotificationsPanel;
